import React, { MouseEvent, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Align } from '@progress/kendo-react-popup';
import cn from 'clsx';
import styles from './index.module.scss';
import Button from '@components-clean/core/button';
import { DropDownListController } from '@components-clean/core/drop-down-list';
import {
    ClientGridFilter,
    ClientGridFilterValues,
    DataType,
    FilterOperators,
    GridFilter,
    GridSearch,
} from '@components-clean/core/grid/grid-custom-filter/grid-custom-filter.interface';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { Popup } from '@components-clean/core/popup/popup';
import { useTranslation } from '@hooks/index';

type SupportedFilterOperator = typeof FilterOperators.Includes | typeof FilterOperators.DoesNotInclude;

type OperatorSelectItem = { value: SupportedFilterOperator; label: string };
type ValueDropDownItem = { value: string; label: string };

type FormValues = {
    operator: OperatorSelectItem | null;
    values: ValueDropDownItem[];
};

type Props = {
    anchor: HTMLElement | null | undefined;
    anchorAlign?: Align;
    popupAlign?: Align;
    show: boolean;
    filterValue: string | boolean | GridFilter | GridSearch | ClientGridFilterValues | null;
    onCancel: (e?: MouseEvent<HTMLButtonElement>, clearFilter?: boolean) => void;
    onSelect: (data: GridFilter | ClientGridFilter, e: MouseEvent<HTMLButtonElement>) => void;
    lovData: string[] | undefined;
};

const GridLovMultiSelectFilter = ({
    anchor,
    anchorAlign,
    popupAlign,
    show,
    filterValue,
    onCancel,
    onSelect,
    lovData,
}: Props) => {
    const { t } = useTranslation();

    const availableOperators: OperatorSelectItem[] = useMemo(() => {
        return [
            { value: FilterOperators.Includes, label: t('includes') },
            { value: FilterOperators.DoesNotInclude, label: t('doesNotInclude') },
        ];
    }, [t]);

    const availableMultiSelectItems: ValueDropDownItem[] = useMemo(() => {
        return (lovData ?? []).map((s) => ({
            value: s,
            label: s,
        }));
    }, [lovData]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = useForm<FormValues>({
        mode: 'all',
        defaultValues: {
            operator: availableOperators[0],
            values: [],
        },
    });

    const selectedOperator = useWatch({ control, name: 'operator' });
    const selectedValues = useWatch({ control, name: 'values' });

    const submitDisabled = selectedOperator == null || selectedValues.length === 0 || isSubmitting;

    const onSubmit = ({ operator, values }: FormValues, e: MouseEvent<HTMLButtonElement>) => {
        // Shouldn't be possible due to validation, but check anyway...
        if (operator == null || values.length === 0) {
            return;
        }

        const filter: GridFilter = {
            logic: null,
            filters: values.map(({ value }) => ({
                operator: operator.value,
                value,
                dataType: DataType.String,
            })),
        };
        onSelect(filter, e);
    };

    const onClear = (e: MouseEvent<HTMLButtonElement>) => {
        reset();
        onCancel(e);
    };

    return (
        <Popup
            data-testid="grid-filter-menu"
            anchor={anchor}
            show={show}
            anchorAlign={anchorAlign}
            popupAlign={popupAlign}
            onBlur={() => onCancel(undefined, false)}
            popupClass="k-rounded-md"
            collision={{ vertical: 'flip', horizontal: 'fit' }}
        >
            <form>
                <div className={cn(styles.formContainer, 'k-d-flex k-flex-col')}>
                    <div className={cn(styles.header, 'k-d-flex k-align-items-center')}>
                        <span className="k-icon k-font-icon k-i-filter" />
                        <div>{t('filter')}</div>
                    </div>

                    <div className={cn(styles.body, 'k-d-flex k-flex-col')}>
                        <DropDownListController
                            data-testid="grid-filter-operator"
                            control={control}
                            name="operator"
                            data={availableOperators}
                            dataItemKey="value"
                            textField="label"
                            clearButton={false}
                            fillMode="flat"
                        />

                        <MultiSelectController
                            data-testid="grid-filter-values"
                            control={control}
                            name="values"
                            data={availableMultiSelectItems}
                            dataItemKey="value"
                            textField="label"
                            isEditing
                            allowCustom={false}
                            fillMode="flat"
                        />
                    </div>

                    <div className={cn(styles.footer, 'k-d-flex')}>
                        <Button
                            data-testid="grid-filter-cancel"
                            type="button"
                            onClick={onClear}
                            disabled={!filterValue}
                        >
                            {t('clear')}
                        </Button>
                        <Button
                            data-testid="grid-filter-confirm"
                            type="button"
                            primary
                            onClick={(e) => handleSubmit((values) => onSubmit(values, e))()}
                            disabled={submitDisabled}
                        >
                            {t('apply')}
                        </Button>
                    </div>
                </div>
            </form>
        </Popup>
    );
};

export default GridLovMultiSelectFilter;
